// Single source of truth for the RBAC section keys, matched to the exact
// titles/groupings used in the left sidebar (see components/Sidebar.js).
// Permission editors (UserManagement.js, OrgManagement.js) render SECTION_LABELS
// instead of the raw backend key so an admin can tell which sidebar pages a
// grant actually governs — e.g. granting 'networking' covers Services,
// Endpoints, Ingresses AND Network Policies, not just a page literally named
// "Networking".
export const ALL_SECTIONS = [
  'alerts', 'clusters', 'servers', 'workloads',
  'networking', 'storage', 'configuration', 'gpu',
  'metrics', 'playbooks', 'vpn',
];

export const ACTIONS = ['view', 'create', 'edit', 'delete'];

// `covers` must name the pages EXACTLY as the sidebar labels them, because that is the only
// way an admin can tell what a grant does. Verified against Sidebar.js menuSections and its
// hasSection() guards — if you rename a sidebar item, update it here in the same change.
export const SECTION_LABELS = {
  alerts:        { label: 'Alerts',        covers: 'Alerts, Alert Search' },
  // App Catalog is gated on this section too, and was missing here.
  clusters:      { label: 'Cluster',       covers: 'Manage Clusters, App Catalog, Namespaces, Nodes, Events' },
  servers:       { label: 'Servers',       covers: 'Servers' },
  // "Compute (Instances)" was renamed to "My Deployments" — My Workloads, My Services,
  // Images — which this section still gates, alongside the cluster-wide Workloads tree.
  // "My Pods" (a separate list of bare-Pod deploys) was folded into My Workloads as
  // regular rows — it was a near-empty, rarely-used page on its own, and Pod is now just
  // one more Kind value there. "My Services" is a real, separate page: the K8s Service
  // SarvKube created for each workload (name/type/cluster IP/ports), not a column buried
  // in the Workloads table.
  workloads:     { label: 'Workloads',     covers: 'Pods, Deployments, StatefulSets, DaemonSets, Jobs, CronJobs, and My Deployments (My Workloads, My Services, Images)' },
  networking:    { label: 'Network',       covers: 'Services, Endpoints, Ingresses, Network Policies' },
  // Sidebar spells these out rather than abbreviating.
  storage:       { label: 'Storage',       covers: 'Persistent Volume Claims, Persistent Volumes, Storage Classes' },
  configuration: { label: 'Configuration', covers: 'ConfigMaps, Secrets, HPA, Resource Quotas, Limit Ranges, Access Control (Service Accounts, Roles, Cluster Roles, Role Bindings, Cluster Role Bindings)' },
  gpu:           { label: 'GPU',           covers: 'GPU Servers — also requires the org to be granted specific GPU servers under Organizations → GPU Server Access' },
  metrics:       { label: 'Metrics',       covers: 'Metrics' },
  playbooks:     { label: 'Playbooks',     covers: 'Playbooks' },
  vpn:           { label: 'VPN',           covers: 'VPN Profiles' },
};

export function sectionLabel(section) {
  return SECTION_LABELS[section]?.label || section;
}

export function sectionCovers(section) {
  return SECTION_LABELS[section]?.covers || '';
}
